import { EnDash, NonBreakingSpace, WidgetWrapper } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';
import { useQueryClient } from '@tanstack/react-query';
import { useAppParams } from '~/helper/useAppParams';
import { invalidateEvaluationQueries } from '~/helper/invalidatePropertyQueries';
import { clsx } from 'clsx';
import { useTranslate } from '@tolgee/react';
import { roundAndFormat } from '~/helper/number';
import { Button } from '~/components/ui/Button';
import { ActionButton } from '~/components/button/ActionButton';
import { useBackendMutation } from '~/helper/useBackendMutation';
import { MutationReference } from '@agentselly/immodossier-shared';
import { useEffect, useState, useCallback } from 'react';
import { Reload as ReloadIcon } from '~/generated/svg/Reload';
import { Clock as ClockIcon } from '~/generated/svg/Clock';
import type { IaziEvaluation } from '@agentselly/immodossier-shared/dist/model/IaziEvaluation';

// Helper function to calculate percentage change
function calculatePercentageChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return newValue === 0 ? 0 : 100;
  return ((newValue - oldValue) / oldValue) * 100;
}

interface Props {
  readonly estimatedValue: number;
  readonly estimatedLowerValue: number;
  readonly estimatedUpperValue: number;
  readonly evaluationTimestamp: number;
  readonly evaluationType: string;
  readonly landArea?: number;
  readonly link: string;
  readonly evaluationValueDifference: {
    readonly text: string;
    readonly textLinks: { readonly text: string; readonly link: string }[];
    readonly buttons?: {
      readonly label: string;
      readonly link: string;
    }[];
    readonly link?: string;
  };
  readonly realEstateLocationId: string;
  readonly realContactId: string;
  readonly differentEvaluationValueInPercent?: number;
  readonly showNotification?: boolean;
}

interface ValueChangeBadgeProps {
  readonly change: number;
  readonly showZero: boolean;
}

function ValueChangeBadge({ change, showZero }: ValueChangeBadgeProps) {
  const { t } = useTranslate();
  const isZero = change === 0;
  const isPositive = change > 0;

  const formattedChange = isZero
    ? ''
    : `${isPositive ? '+' : ''}${change.toFixed(2)}%`;

  if (isZero && !showZero) {
    return null;
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        {
          'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 dark:text-gray-200 dark:bg-gray-700 dark:text-gray-200':
            isZero,
          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400':
            isPositive,
          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400':
            !isZero && !isPositive,
        },
      )}
    >
      {!isZero && (
        <svg
          className="w-3 h-3 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={
              isPositive
                ? 'M5 10l7-7m0 0l7 7m-7-7v18'
                : 'M19 14l-7 7m0 0l-7-7m7 7V3'
            }
          />
        </svg>
      )}
      <span>{formattedChange}</span>
      {isZero && showZero && (
        <span className="ml-1 text-[9px]">
          {t('evaluation_detailed_evaluation_widget_unchanged')}
        </span>
      )}
    </span>
  );
}

export function DetailedEvaluationWidget(props: Props) {
  const { t } = useTranslate();
  const queryClient = useQueryClient();
  const { dossierId, propertyId, contactId } = useAppParams();
  const searchParams = new URLSearchParams(window.location.search);
  const shouldAutoRefresh = searchParams.get('reload') === 'true';

  const [
    error,
    setError,
  ] = useState<string | null>(null);

  const [
    iaziData,
    setIaziData,
  ] = useState<IaziEvaluation | null>(null);

  const refreshMutation = useBackendMutation<
    MutationReference.ImmoDossierEvaluate,
    { realEstateLocationId: string }
  >(MutationReference.ImmoDossierEvaluate, {
    onSuccess: async (data) => {
      setError(null);
      if (data.iazi) {
        setIaziData(data.iazi);
      }
      await invalidateEvaluationQueries(queryClient, {
        dossierId: dossierId ?? '',
        propertyId,
        contactId,
      });
    },
    onError: () => {
      setError(
        t('evaluation_detailed_evaluation_widget_refresh_evaluation_error'),
      );
    },
  });

  const handleRefresh = useCallback(() => {
    refreshMutation.mutate({
      realEstateLocationId: props.realEstateLocationId,
    });
  }, [
    refreshMutation,
    props.realEstateLocationId,
  ]);

  useEffect(() => {
    if (shouldAutoRefresh) {
      handleRefresh();
      // Remove the parameter after triggering refresh
      searchParams.delete('reload');
      const newUrl =
        window.location.pathname +
        (searchParams.toString() ? '?' + searchParams.toString() : '') +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
  }, [
    shouldAutoRefresh,
    handleRefresh,
    searchParams,
  ]);

  const links = props.evaluationValueDifference.textLinks.map(
    (textLink, index) => (
      <AppLink
        key={index}
        className="font-bold underline bg-gray"
        to={textLink?.link}
      >
        {t(textLink?.text)}
      </AppLink>
    ),
  );

  const textWithLinks = t(props.evaluationValueDifference.text, {
    link1: links[0],
    link2: links[1],
  } as any);

  const iaziValue = iaziData?.value ?? props.estimatedValue;
  const iaziLowerValue = iaziData?.lowConfInt50 ?? props.estimatedLowerValue;
  const iaziUpperValue = iaziData?.hiConfInt50 ?? props.estimatedUpperValue;
  const iaziEvaluationDate =
    iaziData?.value && iaziData.value > 0
      ? Date.now()
      : props.evaluationTimestamp;
  const valueChange = iaziData?.value
    ? Number(calculatePercentageChange(props.estimatedValue, iaziData.value))
    : props.differentEvaluationValueInPercent;

  const safeValueChange = Number.isNaN(valueChange) ? null : valueChange;

  const emailMutation = useBackendMutation<
    MutationReference.ImmoDossierEvaluationMail,
    { realEstateLocationId: string; realContactId: string }
  >(MutationReference.ImmoDossierEvaluationMail, {
    onError: () => {
      setError(t('evaluation_detailed_evaluation_widget_email_error'));
    },
  });

  const handleEmailRequest = () => {
    emailMutation.mutate({
      realContactId: props.realContactId,
      realEstateLocationId: props.realEstateLocationId,
    });
  };

  return (
    <WidgetWrapper>
      <div className="h-full flex flex-col gap-y-6">
        <div className="w-full space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-300 dark:text-gray-200">
              {t('evaluation_detailed_evaluation_widget_title')}
            </h2>
            <button
              onClick={handleRefresh}
              disabled={refreshMutation.isPending}
              className="flex items-center px-4 py-2 text-sm font-medium bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus-visible:outline-none disabled:bg-white dark:disabled:bg-gray-800 dark:bg-gray-800 dark:disabled:bg-gray-800 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out"
              aria-label={t(
                'evaluation_detailed_evaluation_widget_refresh_evaluation_tooltip',
              )}
            >
              <ReloadIcon
                className={`h-5 w-5 ${
                  refreshMutation.isPending ? 'animate-spin' : ''
                }`}
              />
              <span className="sr-only">
                {t(
                  'evaluation_detailed_evaluation_widget_refresh_evaluation_tooltip',
                )}
              </span>
              {props.showNotification && !refreshMutation.isSuccess && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-orange-500 ring-2 ring-white" />
              )}
            </button>
          </div>

          <div className="flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            <span>
              {t('evaluation_detailed_evaluation_widget_last_update_text')}:
            </span>
            <span className="ml-2 font-medium">
              {iaziEvaluationDate
                ? formatDate(new Date(iaziEvaluationDate))
                : t('evaluation_detailed_evaluation_widget_no_evaluation_date')}
            </span>
          </div>
          {error && (
            <div
              role="alert"
              className="alert alert-error rounded-md mt-2 md:self-start"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="-0.625 -0.625 20 20"
                height="20"
                width="20"
                id="Information-Circle--Streamline-Ultimate"
              >
                <desc>
                  Information Circle Streamline Icon: https://streamlinehq.com
                </desc>
                <path
                  stroke="currentcolor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.1328125 12.890625h-0.5859375c-0.31078125 0 -0.60890625 -0.1234375 -0.828671875 -0.343203125S9.375 12.02953125 9.375 11.71875v-2.9296875c0 -0.155390625 -0.06171875 -0.304453125 -0.171640625 -0.414296875 -0.10984375 -0.10992187499999999 -0.25890625 -0.171640625 -0.414296875 -0.171640625h-0.5859375"
                  strokeWidth="1.25"
                ></path>
                <path
                  stroke="currentcolor"
                  d="M9.08203125 5.859375c-0.161796875 0 -0.29296875 -0.1311640625 -0.29296875 -0.29296875s0.131171875 -0.29296875 0.29296875 -0.29296875"
                  strokeWidth="1.25"
                ></path>
                <path
                  stroke="currentcolor"
                  d="M9.08203125 5.859375c0.161796875 0 0.29296875 -0.1311640625 0.29296875 -0.29296875s-0.131171875 -0.29296875 -0.29296875 -0.29296875"
                  strokeWidth="1.25"
                ></path>
                <path
                  stroke="currentcolor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.375 18.1640625c4.8540624999999995 0 8.7890625 -3.9350000000000005 8.7890625 -8.7890625S14.229062500000001 0.5859375 9.375 0.5859375 0.5859375 4.5209375000000005 0.5859375 9.375 4.5209375000000005 18.1640625 9.375 18.1640625Z"
                  strokeWidth="1.25"
                ></path>
              </svg>
              <div>
                <p className="font-bold">{t(error)}</p>
              </div>
            </div>
          )}
          <p>
            {t(`evaluation_detailed_evaluation_widget_evaluation_explanation`, {
              evaluationDescription: t(
                `evaluation_type_${props.evaluationType}`,
              ),
            })}
          </p>
        </div>
        <div className="flex justify-between gap-x-8 h-full">
          <div className="w-full lg:w-1/2 flex flex-col justify-between flex-1">
            <div className="flex-1">
              <div className="flex flex-col">
                {t('evaluation_detailed_evaluation_widget_average_text')}:
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold">
                    {`CHF${NonBreakingSpace}${roundAndFormat(iaziValue)}`}
                  </span>
                  {safeValueChange !== null &&
                    safeValueChange !== undefined && (
                      <ValueChangeBadge
                        change={safeValueChange}
                        showZero={iaziData?.value !== undefined}
                      />
                    )}
                </div>
              </div>

              <div className="flex flex-col mt-4">
                {t(
                  'evaluation_detailed_evaluation_widget_value_range_low_standard_text',
                )}
                :
                <PriceRange
                  className="text-lg font-bold"
                  from={iaziLowerValue}
                  to={iaziValue}
                />
              </div>

              <div className="flex flex-col mt-2">
                {t(
                  'evaluation_detailed_evaluation_widget_value_range_high_standard_text',
                )}
                :
                <PriceRange
                  className="text-lg font-bold"
                  from={iaziValue}
                  to={iaziUpperValue}
                />
              </div>

              {props.landArea ? (
                <div className="flex flex-col mt-2">
                  {t(
                    'evaluation_detailed_evaluation_widget_price_per_area_text',
                  )}
                  :
                  <PriceRange
                    className="text-lg font-bold"
                    from={iaziLowerValue / props.landArea}
                    to={iaziUpperValue / props.landArea}
                  />
                </div>
              ) : null}
            </div>
            <ActionButton
              onClick={handleEmailRequest}
              isLoading={emailMutation.isPending}
              className="w-full mt-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                />
              </svg>
              {emailMutation.isPending
                ? t(
                    'evaluation_detailed_evaluation_widget_email_button_sending',
                  )
                : t('evaluation_detailed_evaluation_widget_email_button')}
              {emailMutation.isSuccess && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-green-600"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </ActionButton>
          </div>

          <div className="hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-between flex-1">
            <div className="bg-primary-200 dark:bg-primary/20 rounded-xl">
              <div className="p-4">
                <p>{textWithLinks}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="block lg:hidden">
          <div className="bg-primary-200 dark:bg-primary/20 rounded-xl">
            <div className="p-4">
              <p>{textWithLinks}</p>
            </div>
          </div>
        </div>
        <div className="flex w-full gap-x-4">
          <AppLink
            to={props.link}
            className="self-start"
          >
            <Button
              primary
              showArrow
            >
              {t('evaluation_detailed_evaluation_widget_button_text')}
            </Button>
          </AppLink>
          {props.evaluationValueDifference.buttons?.length ? (
            <div className="space-y-2 xl:space-y-0 xl:flex xl:space-x-8">
              {props.evaluationValueDifference.buttons.map((button, index) => (
                <AppLink
                  key={index}
                  to={button?.link}
                  className="inline-flex w-full xl:w-auto"
                >
                  <ActionButton className="w-full xl:w-auto py-2.5">
                    {t(button?.label)}
                  </ActionButton>
                </AppLink>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </WidgetWrapper>
  );
}

function PriceRange(props: {
  readonly className?: string;
  readonly from: number;
  readonly to: number;
  readonly oldFrom?: number;
  readonly oldTo?: number;
}) {
  const from = roundAndFormat(props.from);
  const to = roundAndFormat(props.to);

  const fromChange = props.oldFrom
    ? Number(calculatePercentageChange(props.oldFrom, props.from))
    : null;
  const toChange = props.oldTo
    ? Number(calculatePercentageChange(props.oldTo, props.to))
    : null;

  return (
    <span className={clsx(props.className)}>
      {`CHF${NonBreakingSpace}${from}`}
      {fromChange !== null && (
        <span
          className={`ml-1 text-sm ${
            fromChange >= 0 ? 'text-green-500' : 'text-red-500'
          }`}
        >
          ({fromChange > 0 ? '+' : ''}
          {fromChange.toFixed(2)}%)
        </span>
      )}
      {` ${EnDash} ${to}`}
      {toChange !== null && (
        <span
          className={`ml-1 text-sm ${
            toChange >= 0 ? 'text-green-500' : 'text-red-500'
          }`}
        >
          ({toChange > 0 ? '+' : ''}
          {toChange.toFixed(2)}%)
        </span>
      )}
    </span>
  );
}

const formatDate = new Intl.DateTimeFormat('de-CH', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format;
